// CSV Export utility
import * as XLSX from 'xlsx';

const escapeCSV = (val) => {
  if (val === null || val === undefined) return '';
  const str = String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

export const exportToCSV = (data, filename, columns) => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }
  
  // columns is array of { key, label, format? }
  const headers = columns.map(c => c.label).join(',');
  const rows = data.map(row => 
    columns.map(c => {
      let val = row[c.key];
      if (c.format) val = c.format(val, row);
      return escapeCSV(val);
    }).join(',')
  );
  
  const csv = [headers, ...rows].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Predefined column sets per entity
export const CSV_COLUMNS = {
  products: [
    { key: 'name', label: 'Name' },
    { key: 'category', label: 'Category' },
    { key: 'brand', label: 'Brand' },
    { key: 'model', label: 'Model' },
    { key: 'cost', label: 'Cost' },
    { key: 'price', label: 'Price' },
    { key: 'warranty', label: 'Warranty (months)' },
    { key: 'serials', label: 'In Stock', format: (v) => (v || []).filter(s => s.status === 'In Stock').length },
    { key: 'serials', label: 'Total Serials', format: (v) => (v || []).length }
  ],
  customers: [
    { key: 'name', label: 'Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'address', label: 'Address' },
    { key: 'nic', label: 'NIC' },
    { key: 'created_at', label: 'Created', format: (v) => v ? new Date(v).toLocaleDateString() : '' }
  ],
  invoices: [
    { key: 'id', label: 'Invoice ID', format: (v) => v.slice(0, 8).toUpperCase() },
    { key: 'customer_id', label: 'Customer ID' },
    { key: 'items', label: 'Item Count', format: (v) => (v || []).length },
    { key: 'total', label: 'Total' },
    { key: 'discount', label: 'Discount' },
    { key: 'paid_amount', label: 'Paid' },
    { key: 'payment_method', label: 'Payment Method' },
    { key: 'status', label: 'Status' },
    { key: 'notes', label: 'Notes' },
    { key: 'created_at', label: 'Created', format: (v) => v ? new Date(v).toLocaleDateString() : '' }
  ],
  rmas: [
    { key: 'id', label: 'RMA ID', format: (v) => v.slice(0, 8).toUpperCase() },
    { key: 'customer_id', label: 'Customer ID' },
    { key: 'product_id', label: 'Product ID' },
    { key: 'serial_original', label: 'Original Serial' },
    { key: 'serial_replacement', label: 'Replacement Serial' },
    { key: 'status', label: 'Status' },
    { key: 'issue', label: 'Issue' },
    { key: 'standby_serial', label: 'Standby Serial' },
    { key: 'standby_returned', label: 'Standby Returned' },
    { key: 'supplier_rma', label: 'Supplier RMA' },
    { key: 'created_at', label: 'Created', format: (v) => v ? new Date(v).toLocaleDateString() : '' }
  ],
  repairs: [
    { key: 'id', label: 'Repair ID', format: (v) => v.slice(0, 8).toUpperCase() },
    { key: 'customer_id', label: 'Customer ID' },
    { key: 'device', label: 'Device' },
    { key: 'issue', label: 'Issue' },
    { key: 'status', label: 'Status' },
    { key: 'cost', label: 'Cost' },
    { key: 'paid', label: 'Paid' },
    { key: 'created_at', label: 'Created', format: (v) => v ? new Date(v).toLocaleDateString() : '' }
  ],
  grns: [
    { key: 'grn_number', label: 'GRN #' },
    { key: 'supplier_name', label: 'Supplier' },
    { key: 'invoice_number', label: 'Supplier Invoice', format: (v) => v || 'No Supplier Invoice' },
    { key: 'received_date', label: 'Received Date' },
    { key: 'items', label: 'Item Count', format: (v) => (v || []).length },
    { key: 'items', label: 'Total Value', format: (v) => (v || []).reduce((s, i) => s + (i.cost * i.qty), 0).toFixed(2) },
    { key: 'payment_status', label: 'Payment Status' },
    { key: 'due_date', label: 'Due Date' },
    { key: 'created_by_name', label: 'Created By' }
  ]
};

// Trigger JSON download
export const downloadJSON = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Read uploaded JSON file
export const readJSONFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        resolve(JSON.parse(e.target.result));
      } catch (err) {
        reject(new Error('Invalid JSON file'));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

// ---------- Excel (XLSX) exports ----------

// Generic single-sheet Excel export.
export const exportToExcel = (data, filename, columns, sheetName = 'Sheet1') => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }
  const rows = data.map((row) => {
    const out = {};
    columns.forEach((c) => {
      let v = row[c.key];
      if (c.format) v = c.format(v, row);
      out[c.label] = v === null || v === undefined ? '' : v;
    });
    return out;
  });
  const ws = XLSX.utils.json_to_sheet(rows, { header: columns.map((c) => c.label) });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
};

// P&L export. `pl` matches /reports/profit-loss response.
// `expenses` (optional) is the filtered expense list shown on the page.
export const exportPLToExcel = (pl, expenses = [], filename = 'profit_loss') => {
  if (!pl) {
    alert('Profit & Loss data not loaded yet');
    return;
  }
  const wb = XLSX.utils.book_new();

  // Sheet 1: Summary
  const summary = [
    ['PC NERDS — Profit & Loss Report'],
    [],
    ['From', pl.from || ''],
    ['To', pl.to || ''],
    [],
    ['Revenue', pl.revenue || 0],
    ['Invoices Counted', pl.invoice_count || 0],
    ['Total Expenses (Approved)', pl.total_expenses || 0],
    ['Net Profit', pl.net_profit || 0],
    [],
    ['Outstanding Customer Payments', pl.outstanding_customer_payments || 0],
    ['Outstanding Supplier Credits', pl.outstanding_supplier_credits || 0],
    ['Overdue Supplier Credits', pl.overdue_supplier_credits || 0],
  ];
  const wsSummary = XLSX.utils.aoa_to_sheet(summary);
  wsSummary['!cols'] = [{ wch: 36 }, { wch: 18 }];
  XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

  // Sheet 2: Expenses by category
  const cats = pl.expenses_by_category || [];
  const catRows = cats.map((c) => ({ Category: c.category, Count: c.count, Amount: c.amount }));
  if (catRows.length === 0) catRows.push({ Category: '— No expenses —', Count: 0, Amount: 0 });
  const wsCat = XLSX.utils.json_to_sheet(catRows, { header: ['Category', 'Count', 'Amount'] });
  wsCat['!cols'] = [{ wch: 28 }, { wch: 10 }, { wch: 14 }];
  XLSX.utils.book_append_sheet(wb, wsCat, 'By Category');

  // Sheet 3: Expense list (if provided)
  if (expenses && expenses.length > 0) {
    const rows = expenses.map((e) => ({
      'Expense #': e.exp_number || '',
      Date: e.date,
      Category: e.category,
      Description: e.description,
      Amount: e.amount,
      Payment: e.payment_method,
      Status: e.status,
      'Linked RMA': e.linked_rma_id || '',
      'Entered By': e.created_by_name || '',
      Notes: e.notes || '',
    }));
    const wsList = XLSX.utils.json_to_sheet(rows);
    wsList['!cols'] = [
      { wch: 12 }, { wch: 12 }, { wch: 18 }, { wch: 36 },
      { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 18 }, { wch: 18 }, { wch: 30 },
    ];
    XLSX.utils.book_append_sheet(wb, wsList, 'Expense Detail');
  }

  XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
};

// P&L printable PDF (uses browser print -> "Save as PDF").
export const exportPLToPDF = (pl, expenses = [], currency = 'LKR') => {
  if (!pl) {
    alert('Profit & Loss data not loaded yet');
    return;
  }
  const fmt = (n) =>
    `${currency} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const cats = pl.expenses_by_category || [];
  const expRows = (expenses || [])
    .map(
      (e) => `
      <tr>
        <td>${e.exp_number || ''}</td>
        <td>${e.date || ''}</td>
        <td>${e.category || ''}</td>
        <td>${(e.description || '').replace(/</g, '&lt;')}</td>
        <td style="text-align:right">${fmt(e.amount)}</td>
        <td>${e.status || ''}</td>
      </tr>`,
    )
    .join('');

  const html = `<!DOCTYPE html><html><head><title>Profit & Loss</title>
  <style>
    body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#1a1a1a;padding:30px;}
    h1{margin:0 0 4px 0;font-size:22px;}
    .muted{color:#666;font-size:12px;margin-bottom:24px;}
    .grid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-bottom:24px;}
    .card{border:1px solid #e2e2e2;border-radius:8px;padding:12px;}
    .card .label{font-size:11px;text-transform:uppercase;color:#777;}
    .card .value{font-size:18px;font-weight:600;margin-top:4px;}
    table{width:100%;border-collapse:collapse;margin-top:8px;font-size:12px;}
    th,td{border-bottom:1px solid #eee;padding:6px 8px;text-align:left;}
    th{background:#f7f7f7;}
    h2{font-size:14px;margin:24px 0 6px 0;}
    .pos{color:#059669;} .neg{color:#dc2626;}
    @media print{@page{margin:14mm;}}
  </style></head><body>
    <h1>PC NERDS — Profit &amp; Loss</h1>
    <div class="muted">Period: ${pl.from || '—'} to ${pl.to || '—'} • Generated ${new Date().toLocaleString()}</div>
    <div class="grid">
      <div class="card"><div class="label">Revenue</div><div class="value pos">${fmt(pl.revenue)}</div></div>
      <div class="card"><div class="label">Total Expenses</div><div class="value neg">${fmt(pl.total_expenses)}</div></div>
      <div class="card"><div class="label">Net Profit</div><div class="value ${pl.net_profit >= 0 ? 'pos' : 'neg'}">${fmt(pl.net_profit)}</div></div>
      <div class="card"><div class="label">Outstanding Customer</div><div class="value">${fmt(pl.outstanding_customer_payments)}</div></div>
      <div class="card"><div class="label">Outstanding Supplier</div><div class="value">${fmt(pl.outstanding_supplier_credits)}</div></div>
      <div class="card"><div class="label">Invoices Counted</div><div class="value">${pl.invoice_count || 0}</div></div>
    </div>
    <h2>Expenses by Category</h2>
    <table><thead><tr><th>Category</th><th style="text-align:right">Count</th><th style="text-align:right">Amount</th></tr></thead><tbody>
      ${cats.length === 0
        ? '<tr><td colspan="3" style="text-align:center;color:#888">No expenses in this period</td></tr>'
        : cats.map((c) => `<tr><td>${c.category}</td><td style="text-align:right">${c.count}</td><td style="text-align:right">${fmt(c.amount)}</td></tr>`).join('')}
    </tbody></table>
    ${expRows ? `<h2>Expense Detail</h2>
      <table><thead><tr><th>EXP#</th><th>Date</th><th>Category</th><th>Description</th><th style="text-align:right">Amount</th><th>Status</th></tr></thead>
      <tbody>${expRows}</tbody></table>` : ''}
    <script>window.onload=()=>{setTimeout(()=>{window.print();},250);};</script>
  </body></html>`;
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) { alert('Pop-up blocked. Please allow pop-ups to export PDF.'); return; }
  win.document.write(html);
  win.document.close();
};
